/**
 * Landing page translations: landing, cookieConsent, abuse, kubernetes,
 * vserver, productsOverview, objectStorage, emailHosting, webhosting,
 * comingSoon, pricingCalculator
 *
 * Legal documents live in ./legal.ts
 */

import { VSERVER_SMTP_BLOCK_DATE } from '@/legal/vserverSmtpBlockDate'

export const landing = {
  hero: {
    eyebrow: 'Domains · Email · VServers · Web hosting',
    titleGradient: 'Domain, email, server.',
    titleSuffix: 'Hosted in Germany.',
    subtitle: 'Everything your business needs online, from one provider: your domain, email on your own address for €1.99 a month and VServers from €7.99. Transparent prices, no hidden fees, personal support in German or English.',
    ctaPrimary: 'Check your domain',
    ctaSecondary: 'See pricing',
    ctaNote: 'All prices incl. VAT · No hidden fees',
    chips: {
      germanServers: 'Servers in Germany',
      support: 'Personal support',
      transparentPrices: 'Transparent pricing',
      hourlyVserver: 'VServers billed hourly'
    }
  },
  bundle: {
    title: 'Domain, email and VServer from one provider',
    subtitle: 'Start with your domain and add what you need. Every product on its own, no bundles you never use.',
    domain: {
      title: 'Domain',
      priceFrom: '.de from {price}/year',
      description: 'Claim your name online. You see the price of every extension before you buy.',
      features: {
        dns: 'DNS management in the customer portal',
        dynDns: 'DynDNS included',
        perTld: 'Clear prices per extension'
      },
      cta: 'Check domain'
    },
    email: {
      title: 'Email on your own domain',
      priceFrom: '€1.99 per domain per month',
      description: 'Professional addresses on your domain, hosted in Germany.',
      features: {
        mailboxes: 'Up to 1,000 mailboxes, 10 GB pooled storage',
        outbound: '1,000 outbound emails per month included',
        protocols: 'Webmail, IMAP, SMTP and JMAP',
        teams: 'Aliases, lists and delegation'
      },
      note: 'Extra storage or sending is billed transparently by usage.',
      cta: 'Set up email'
    },
    vserver: {
      title: 'VServer',
      priceFrom: 'from €7.99 per month',
      description: 'Your own server with root access, for websites, applications or internal tools.',
      features: {
        billing: 'Billed hourly, never more than the monthly price',
        term: 'No minimum term',
        network: 'IPv4 and IPv6 included',
        tools: '2 to 5 snapshots depending on the plan, web console'
      },
      cta: 'See plans'
    },
    webhostingHint: 'Just need a PHP website? Our web hosting comes with MariaDB and SFTP.',
    webhostingLink: 'Go to web hosting'
  },
  k8sTeaser: {
    eyebrow: 'For developers',
    title: 'Kubernetes namespaces, no cluster to run',
    text: 'Deploying containers? Get your own Kubernetes namespace with kubectl and Helm access, without running a control plane. Fixed plans or pay-as-you-go, with a free entry tier.',
    cta: 'Explore Kubernetes namespaces'
  },
  nav: {
    products: 'Products'
  },
  features: {
    support: 'Personal Support',
    dailyBackups: 'Daily Volume Backups',
    monitoring247: '24/7 Monitoring',
    dnsIncluded: 'DNS Included',
    whoisProtection: 'WHOIS protection optional',
    autoRenewal: 'Auto-Renewal',
    instantRegistration: 'Instant Registration',
    dnsManagement: 'DNS Management',
    dynDns: 'DynDNS Included',
    tlds500: '500+ TLDs',
    haControlPlane: 'HA Control Plane'
  },
  products: {
    domains: {
      title: 'Domains',
      tlds500: '500+ TLDs',
      findYourDomain: 'Secure your domain',
      searchPlaceholder: 'your-dream-domain',
      allTlds: 'All TLDs'
    }
  },
  footer: {
    tagline: 'Cloud infrastructure that scales with you. Kubernetes, VServers, web hosting, mail and domains — from a single source.',
    products: 'Products',
    allProducts: 'All Products',
    pricingCalculator: 'Pricing Calculator',
    tldOverview: 'All Domain Extensions',
    support: 'Support',
    legal: 'Legal',
    knowledgeBase: 'Knowledge Base',
    status: 'Status',
    docs: 'Documentation',
    contact: 'Contact',
    imprint: 'Imprint',
    privacy: 'Privacy',
    terms: 'Terms',
    nis2: 'NIS2 Guidelines',
    sla: 'SLA',
    withdrawal: 'Withdrawal instructions',
    cancellation: 'Verträge hier kündigen',
    reportAbuse: 'Report Abuse',
    cookieSettings: 'Cookie Settings',
    copyright: '© {year} ITSH. All rights reserved.',
    pricesIncludeTax: 'Prices incl. VAT'
  }
} as const

export const cookieConsent = {
  text: 'We use cookies to optimise our advertising. ',
  learnMore: 'Learn more',
  declineBtn: 'Essential only',
  acceptBtn: 'Accept all',
  ariaLabel: 'Cookie consent'
} as const

export const abuse = {
  title: 'Report Abuse',
  subtitle: 'Report suspicious activities or violations on domains we operate, including customer domains, itsh.dev and itsh-apps.dev, or activity such as spam coming from our servers.',
  categories: {
    spam: 'Spam',
    phishing: 'Phishing',
    malware: 'Malware',
    fakeData: 'False Contact Data',
    trademark: 'Trademark Infringement',
    other: 'Other'
  },
  form: {
    email: 'Your Email Address',
    emailPlaceholder: "name{'@'}example.com",
    name: 'Your name',
    nameOptional: 'Your name (optional for "Other")',
    namePlaceholder: 'Jane Doe',
    target: 'What do you want to report?',
    targetDomain: 'A domain',
    targetIp: 'An IP address',
    domain: 'Affected domain',
    ip: 'Affected IP address',
    ipInvalid: 'Please enter a valid IPv4 or IPv6 address.',
    url: 'Exact location of the content (URL)',
    urlOptional: 'Exact location of the content (URL, optional)',
    urlHint: 'Please give the full address of the page complained of, not just the domain.',
    goodFaith: 'I declare, to the best of my knowledge and belief, that the information I have given is accurate and complete.',
    category: 'Type of abuse',
    categoryPlaceholder: 'Please select',
    description: 'Description',
    descriptionPlaceholder: 'Please describe the incident in as much detail as possible...',
    descriptionMinLength: 'Minimum 10 characters required',
    submit: 'Submit Report'
  },
  success: {
    title: 'Report Submitted',
    message: 'Thank you for your report. Our team will review the case and take action if necessary.',
    outOfScope: 'The reported domain is not operated by us. We have recorded your notice, but we are not the correct addressee — please contact the responsible provider.',
    outOfScopeIp: 'The reported IP address does not belong to our servers. We have recorded your report, but we are not the right addressee. Please contact the responsible provider.',
    caseId: 'Reference number: {id}',
    close: 'Close'
  },
  captchaError: 'Captcha verification failed. Please try again.',
  privacyNotice: 'Your data will be processed in accordance with our privacy policy and used only for handling the report.',
  tooManyReports: 'Too many reports. Please wait a moment.'
} as const

export const kubernetes = {
  title: 'Kubernetes by the namespace —',
  titleSuffix: 'no cluster to run',
  eyebrow: 'Managed Kubernetes from Germany — by the namespace',
  subtitle: 'Get a real Kubernetes namespace with full kubectl, Helm and CI/CD access, with no control plane to run, patch or pay for. We operate the cluster; you ship. Start free.',
  cta: 'Get started — free',
  ctaSecondary: 'See pricing',
  chips: {
    germanDatacenters: 'German data centres',
    cheaperThanEks: 'Cheaper than AWS EKS'
  },
  features: {
    title: 'Managed Kubernetes without the ops work',
    security: {
      title: 'Security & Backups',
      description: 'Network isolation, strict quotas and daily backups of volumes and namespace config. Databases need a backup of their own.'
    },
    transparentPricing: {
      title: 'Transparent Pricing',
      description: 'Base plan + metered resource allocation. No surprises.'
    },
    managed: {
      title: 'We manage it for you',
      description: 'Upgrades, patches and 24/7 monitoring.'
    },
    haControlPlane: {
      title: 'Highly Available Control Plane',
      description: 'Redundant control plane with automatic failover for maximum availability.'
    }
  },
  pricing: {
    fixedTiersLabel: 'Fixed Plans',
    title: 'Namespace Pricing',
    subtitle: 'Transparent base price + metered resource allocation.',
    clusterTeaser: 'Dedicated clusters from €399/month.',
    clusterContact: 'Get in touch',
    intervals: {
      month: 'month'
    },
    popular: 'Popular',
    recommended: 'Recommended',
    startNow: 'Get started',
    ownControlPlane: 'Own control plane',
    euDataResidency: 'EU data residency',
    addons: 'Add-ons',
    egressIncluded: '{amount} GiB egress included',
    sharedFeatures: 'All plans include daily backups of volumes and namespace config, 24/7 monitoring and German data centres.',
    databaseBackups: 'For databases that is not enough: the daily copy is taken while they run and does not guarantee a consistent database state. Set up a backup of your own for your databases.',
    databaseBackupsLink: 'Read the guide',
    freeGuidance: 'To try it out — free forever',
    paygGuidance: 'For variable workloads',
    fixedGuidance: 'For predictable workloads'
  },
  payg: {
    subtitle: 'Billed by CPU & RAM usage while your pods run',
    billingNote: "You're billed for the higher of your reserved (requested) CPU & RAM or actual consumption. Storage is billed by provisioned capacity. Egress is billed by actual traffic.",
    baseFee: 'Monthly base fee',
    rates: {
      cpuUnit: '/core-hour',
      memoryUnit: '/GiB-hour',
      storageUnit: '/GiB-month',
      egressUnit: '/GiB'
    },
    features: {
      noMinimum: 'No minimum usage',
      spendingCap: 'Budget alerts & spending notifications'
    }
  },
  costCompare: {
    title: 'Why not just use AWS?',
    subtitle: "You don't need a cluster. You need a namespace.",
    itshLabel: 'ITSH Namespace',
    awsLabel: 'AWS EKS',
    selfHostedLabel: 'Self-Hosted',
    monthlyCost: 'Monthly cost',
    itshCost: 'from €3/month',
    awsCost: 'from $73/month',
    selfHostedCost: 'from €150/month',
    setupTime: 'Setup time',
    itshSetup: '2 minutes',
    awsSetup: '30–60 minutes',
    selfHostedSetup: '2–5 days',
    opsOverhead: 'Ops overhead',
    itshOps: 'None — we manage it',
    awsOps: 'Medium — updates, networking, IAM',
    selfHostedOps: 'High — everything yourself',
    controlPlaneNote: 'AWS EKS charges $73/month just for the control plane — before a single container runs.',
    savingsHighlight: 'cheaper than the AWS EKS control plane',
    sourceLabel: 'Sources',
    sourceTooltip: 'AWS EKS: aws.amazon.com/eks/pricing, as of March 2026. The $73/month figure reflects EKS Standard support. Self-hosted estimates based on a 3-node dedicated-server setup. Setup times are based on experience.'
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: {
      q1: 'What is the difference between a namespace and a cluster?',
      a1: 'A namespace is an isolated area within a shared cluster. You get your own CPU, RAM and storage quotas, network policies and RBAC — without managing your own cluster. Ideal for small to medium teams.',
      q2: 'How much does managed Kubernetes cost compared to AWS?',
      a2: 'AWS EKS charges $73/month just for the control plane, plus compute costs. With ITSH, you can start on the free tier for evaluation, then move to a PAYG namespace from €3/month base fee plus usage-based CPU and RAM billing. That undercuts the AWS EKS control plane, with zero upfront commitment.',
      q3: 'Do I need a DevOps team for Kubernetes?',
      a3: 'No. We handle updates, security patches, monitoring and daily backups of your volumes and namespace config. You deploy your apps via kubectl, Helm or CI/CD. Databases need a backup of their own, and our documentation shows you how to set one up.',
      q4: 'How do I migrate my app to Kubernetes?',
      a4: 'Containerise your app with a Dockerfile, write a Kubernetes manifest or Helm chart, and deploy it to your namespace. Our support team is happy to help you get started.',
      q5: 'Where are the servers located?',
      a5: 'Our Kubernetes clusters run in German data centres. You accept the data processing agreement (Art. 28 GDPR) directly in your account.',
      q6: 'Can I switch from AWS/GCP to ITSH?',
      a6: 'Yes. Kubernetes workloads are portable. Export your manifests, adjust your container registry and ingress configuration, and deploy them to your ITSH namespace. We support standard Kubernetes — no vendor lock-in.',
      q7: 'Is there a free tier?',
      a7: 'Yes. Every account can claim one perpetual free Kubernetes namespace: 200 mCPU, 512 MiB RAM, 1 GiB NFS storage and 10 GiB outbound traffic per month. You verify your phone number; if risk signals fire, a zero-euro card confirmation can be required as well. Available in the EU/EEA, UK, US, Canada, Australia, New Zealand and Switzerland. Upgrade to a paid tier in one click when you outgrow it; your workloads stay live during the upgrade.'
    }
  }
} as const

export const vserver = {
  title: 'VServer',
  titleSuffix: 'everything included',
  eyebrow: 'VPS — Virtual servers',
  subtitle:
    'VPS on dedicated hardware. Snapshots, traffic and direct support are already in the price. No add-ons, no surprise fees.',
  cta: 'See plans — from {price}',
  heroPriceline: 'From {price} per month: {vcpu} vCPU, {ram} GB RAM, {disk} GB NVMe. Monthly with hourly billing up to the monthly price, or paid in advance for 3, 6 or 12 months.',
  heroChips: {
    nvme: 'NVMe SSDs',
    snapshots: 'Snapshots included',
    traffic: 'Traffic included',
    support: 'Direct support'
  },
  features: {
    title: 'What sets ITSH VServers apart',
    nvme: {
      title: 'Dedicated NVMe storage',
      description: 'Enterprise NVMe right in the server — no shared cloud block storage, no throttling.'
    },
    conservative: {
      title: 'Consistent CPU performance',
      description:
        'We run every node with deliberate headroom, so your vCPUs stay responsive under load — never maxed out, never throttled.'
    },
    snapshots: {
      title: 'Snapshots included',
      description: '2 to 5 snapshots per server depending on the plan, restorable at any time. No extra fee per GB.'
    },
    ipv6: {
      title: 'IPv6 /112 per VM',
      description: '65,536 IPv6 addresses per VServer. Plenty for containers, Docker and microservices.'
    },
    firewall: {
      title: 'Firewall rules in the panel',
      description: 'Set your own rules per server in the panel, applied in front of the VM. Until you add one, all traffic is allowed, except outbound port 25 during your first three months (see the FAQ).'
    },
    support: {
      title: 'Direct support',
      description: 'Small team, fast replies. No ticket waiting room.'
    }
  },
  pricing: {
    title: 'Pricing',
    subtitle: 'All prices include 19% VAT. Monthly, you pay the first month when you order; after that, billing is hourly and never exceeds the monthly price. A period of 3, 6 or 12 months is paid in advance and followed by the next. No minimum term. Snapshots and IPv6 /112 in every plan.',
    perMonth: '/month',
    popular: 'Popular',
    startNow: 'Order now',
    compareLink: 'Comparing VPS providers? See the VServer comparison',
    unavailable: 'This plan is currently unavailable. Please try again later.',
    sharedFeatures: 'All plans include traffic, snapshots, IPv6 /112 and direct support.',
    specs: {
      vcpu: '{count} vCPU',
      vcpus: '{count} vCPUs',
      ram: '{amount} GB RAM',
      disk: '{amount} GB NVMe',
      snapshots: '{count} snapshot included | {count} snapshots included',
      ipv4: '1 IPv4 address',
      ipv6: 'IPv6 /112'
    },
    tiers: {
      s: 'VServer S',
      m: 'VServer M',
      l: 'VServer L',
      xl: 'VServer XL'
    }
  },
  faq: {
    title: 'Frequently asked questions',
    items: {
      q1: 'What hardware runs the VServers?',
      a1: 'On dedicated hardware in an EU data centre, with ECC RAM and enterprise NVMe SSDs.',
      q2: 'Are backups included?',
      a2: 'No, a VServer does not include backups. You can keep 2 to 5 snapshots per server depending on the plan, and roll back to any of them. A snapshot sits on the same storage as the server and is not a backup: keep a copy of data you cannot afford to lose somewhere else.',
      q3: 'Can I change my plan later?',
      a3: "Yes. Upgrade or downgrade any time. Disks can only grow, not shrink. On monthly billing, the new plan's hourly rate applies from the moment of the change, and the monthly cap follows the current plan. On a period of 3, 6 or 12 months, the period stays and we charge or refund the difference for the rest of it by the day.",
      q4: 'How does the web console work?',
      a4: 'A secure web console right in the panel — no separate VNC client needed.',
      q5: 'Can I reinstall the operating system?',
      a5: 'Yes. One click reinstalls the operating system from one of our templates. There is no rescue mode at the moment. If SSH stops working, the web console in the customer portal reaches the server even when its network does not work.',
      q6: 'Which operating systems do you provide?',
      a6: 'Debian, Ubuntu, Rocky, Alpine, Arch, Fedora, openSUSE and Flatcar, each in a current version.',
      q7: 'Is traffic included?',
      a7: 'Yes. Inbound and outbound traffic is included in the price — no volume packages, no surcharges. Fair use applies: normal server workloads never hit a limit.',
      q8: 'Can I run my own mail server?',
      a8: `Yes. For the first three months after your first vServer order, outbound port 25 (delivery to other mail servers) is blocked to keep spam off fresh servers. Mail to your mail provider over the submission ports 587 and 465 works at any time. If you need port 25 sooner, request the unblock with a short reason in the customer portal. Customers who already had a vServer before ${VSERVER_SMTP_BLOCK_DATE.en} are not affected.`
    }
  }
} as const

export const vserverCompare = {
  eyebrow: 'For anyone comparing VPS providers',
  title: 'VPS comparison: what actually matters',
  intro:
    'A VPS is more than its price per vCPU. Term, location, billing and who answers when something breaks decide whether a server fits your project. Here is what ITSH includes in every plan, and what it does not yet.',
  tiers: {
    title: 'The plans',
    headers: {
      tier: 'Plan',
      vcpu: 'vCPU',
      ram: 'RAM',
      disk: 'Storage',
      snapshots: 'Snapshots',
      price: 'Maximum per month'
    },
    note: 'Prices incl. 19% VAT. Monthly, the first month is paid when you order, after that we bill hourly, never more than the monthly price. A period of 3, 6 or 12 months is paid in advance and followed by the next.'
  },
  criteria: {
    title: 'What to compare',
    headers: {
      criterion: 'Criterion',
      check: 'What to look for',
      itsh: 'At ITSH, in every plan'
    },
    rows: {
      term: {
        criterion: 'Term',
        check: 'Is there a minimum term that locks you in?',
        itsh: 'No minimum term.'
      },
      billing: {
        criterion: 'Billing',
        check: 'Do you pay full months or actual usage?',
        itsh: 'Monthly: first month at order, then hourly, never more than the monthly price. Or 3, 6 or 12 months in advance.'
      },
      location: {
        criterion: 'Location',
        check: 'Which country are the servers in, and who runs them?',
        itsh: 'Data centre in Germany, operated by a German company.'
      },
      network: {
        criterion: 'IPv4 and IPv6',
        check: 'Is an IPv4 address included, is there IPv6?',
        itsh: '1 IPv4 address and an IPv6 /112 network per server.'
      },
      snapshots: {
        criterion: 'Snapshots',
        check: 'Can you save a state before an update?',
        itsh: '2 to 5 snapshots per server depending on the plan, see the plan table.'
      },
      backups: {
        criterion: 'Backups',
        check: 'Is your data backed up automatically to another location?',
        itsh: 'Not offered yet. See the note below.'
      },
      access: {
        criterion: 'Emergency access',
        check: 'Can you reach the server when SSH stops working?',
        itsh: 'Web console in the customer portal.'
      },
      sizing: {
        criterion: 'Sizes and images',
        check: 'Can you pick custom sizes or boot your own image?',
        itsh: 'Fixed plans S to XL with our operating system templates.'
      },
      support: {
        criterion: 'Support',
        check: 'In which language, and do you reach someone who knows the system?',
        itsh: 'By ticket in German or English, directly from the operator.'
      }
    }
  },
  backupNote: {
    title: 'To be upfront: backups are not available yet',
    text: 'Our VServers do not include automatic backups at the moment. You can keep 2 to 5 snapshots per server depending on the plan, for example before an update. A snapshot is not a replacement for a backup in another location. If your data matters, back it up externally as well.'
  },
  cta: {
    title: 'Your server in Germany, billed by the hour',
    subtitle: 'Pick a plan with no minimum term and no hidden fees. Questions before you order? We answer them personally.',
    primary: 'See plans',
    secondary: 'Ask a question'
  }
} as const

export const productsOverview = {
  title: 'One EU platform for your whole stack.',
  subtitle: 'Kubernetes, VServer, web hosting, e-mail, object storage and domains: managed from one panel, with no hidden fees. The hyperscaler building blocks you actually use, run in the EU, with real support.',
  detailsLink: 'Details & pricing',
  kubernetes: {
    description: 'Full kubectl access without running a cluster — your namespace is ready in minutes.',
    priceFrom: 'Free tier — then from €3/month',
    features: {
      autoScaling: 'Autoscaling',
      loadBalancing: 'Load balancing',
      freeTier: 'Free-forever tier'
    }
  },
  vserver: {
    description: 'Premium VPS with root access and NVMe storage, in four fixed sizes.',
    priceFrom: 'from €7.99/month',
    features: {
      rootAccess: 'Full root access',
      nvmeStorage: 'NVMe SSD storage',
      allInclusive: 'Traffic & snapshots included'
    }
  },
  webhosting: {
    description: 'Reliable hosting with SSD storage, SSL certificates and email mailboxes.',
    priceFrom: 'from €3.49/month',
    features: {
      sftpCron: 'SFTP & cron jobs',
      freeSsl: 'SSL certificates included',
      emailIncluded: 'Email included'
    }
  },
  domains: {
    description: 'Secure your dream domain from over 500 available extensions.',
    priceFrom: '.de from {price}/year',
    features: {
      tlds500: 'Over 500 TLDs',
      dnsManagement: 'DNS management',
      whoisProtection: 'Optional WHOIS protection',
      dynDns: 'DynDNS included'
    }
  },
  objectStorage: {
    description: 'S3-compatible object storage for backups, assets and application data.',
    priceFrom: '{price}/GB/month',
    features: {
      s3Compatible: 'S3-compatible API',
      threeEuRegions: '3 EU regions',
      payAsYouGo: 'Pay-as-you-go'
    }
  },
  emailHosting: {
    description: 'Up to 1,000 mailboxes on your own domain.',
    priceFrom: '€1.99/month per domain',
    features: {
      mailboxes: 'Up to 1,000 mailboxes',
      tenGbStorage: '{storage} GB storage',
      autoSecurity: 'DKIM/SPF/DMARC'
    }
  },
  decisionStrip: {
    title: 'Not sure which fits?',
    items: {
      containers: { question: 'Deploying containers?', product: 'Kubernetes' },
      rootControl: { question: 'Want full root control?', product: 'VServer' },
      website: { question: 'Just need a website and email?', product: 'Web hosting' }
    },
    calculatorPrompt: 'Or price out your setup:',
    calculatorLink: 'Open the pricing calculator',
    supportNote: 'German- and English-speaking support included.'
  }
} as const

export const objectStorage = {
  title: 'S3 storage without the surprise bill',
  eyebrow: 'S3-compatible storage',
  subtitle:
    'Point aws-cli, boto3, or any S3 SDK at our endpoint: no migration, no code changes. {storagePrice}/GB storage, flat {egressPrice}/GB egress, requests included. No setup fee, no minimum term. Choose the region your data lives in.',
  cta: 'Get started',
  ctaNote: 'Free to sign up — you only pay for what you store.',
  ctaPricing: 'See pricing',
  codeProof: {
    bucket: 'your-bucket',
    caption: "That's the whole migration."
  },
  heroChips: {
    s3Compatible: 'S3 Compatible',
    regions: '3 EU Regions',
    gdpr: 'DPA under Art. 28 GDPR',
    payAsYouGo: '{price}/GB storage'
  },
  features: {
    s3Api: {
      title: 'S3-compatible API',
      description: 'Use aws-cli, boto3, or any S3 SDK. No migration required.'
    },
    multiRegion: {
      title: 'Three EU Regions',
      description: 'Nuremberg, Falkenstein and Helsinki. Your data stays in the EU.'
    },
    encryption: {
      title: 'Encryption',
      description: 'TLS-secured transfer. Optional server-side encryption (SSE-C) available.'
    },
    billing: {
      title: 'Simple Billing',
      description: 'Only pay for what you use. No hidden costs, no minimum term.'
    },
    scalable: {
      title: 'Unlimited Scalability',
      description: 'From megabytes to petabytes — no upfront provisioning required.'
    },
    dashboard: {
      title: 'Dashboard Management',
      description: 'Manage buckets and credentials conveniently via the control panel.'
    }
  },
  pricing: {
    title: 'Transparent Pricing',
    subtitle: 'Only pay for what you use. No setup fee, no minimum term.',
    storage: {
      label: 'Storage',
      unit: 'per GB / month'
    },
    egress: {
      label: 'Outgoing Traffic',
      unit: 'per GB'
    },
    requests: {
      label: 'Requests',
      value: 'Included'
    },
    example: {
      title: 'Example Calculation',
      description: '100 GB storage + 50 GB traffic',
      result: '{price} / month'
    },
    calculatorLink: 'Price out your own setup → Pricing calculator'
  },
  regions: {
    title: 'Available Regions',
    euCentral1: {
      name: 'eu-central-1',
      location: 'Nuremberg'
    },
    euCentral2: {
      name: 'eu-central-2',
      location: 'Falkenstein'
    },
    euNorth1: {
      name: 'eu-north-1',
      location: 'Helsinki'
    }
  },
  useCases: {
    title: 'Use Cases',
    backups: {
      title: 'Backups & Archiving',
      description: 'Store automated backups of your applications and databases securely.'
    },
    staticAssets: {
      title: 'Static Assets',
      description: 'Serve images, videos and downloads for websites and apps.'
    },
    appStorage: {
      title: 'Application Data',
      description: 'Direct integration into your applications via the S3 API.'
    },
    archives: {
      title: 'Data Archive',
      description: 'Long-term storage of logs, documents and compliance data.'
    }
  },
  closing: {
    title: 'Create a bucket, start uploading.',
    subtitle: 'No setup fee, no minimum term — store nothing, pay nothing.'
  }
} as const

export const emailHosting = {
  title: 'Up to 1,000 mailboxes. One flat domain price.',
  eyebrow: 'Email Hosting',
  subtitle:
    '€1.99/month per domain: up to 1,000 mailboxes and 5,000 aliases on your own domain, 10 GB shared storage, fast JMAP sync, webmail and IMAP/SMTP included. Hosted in Germany, never scanned for ads.',
  cta: 'Get started for €1.99',
  heroChips: {
    mailboxes: 'Up to 1,000 Mailboxes',
    german: 'German Servers',
    noAds: 'Never scanned for ads',
    dkim: 'DKIM/SPF/DMARC'
  },
  features: {
    mailboxes: {
      title: '1,000 Mailboxes & 5,000 Aliases',
      description: 'Up to 1,000 mailboxes and 5,000 aliases or lists per domain, at no extra cost.'
    },
    autoSecurity: {
      title: 'Automatic Email Security',
      description: 'DKIM, SPF and DMARC are configured automatically. Your emails land in the inbox.'
    },
    protocols: {
      title: 'JMAP, IMAP, SMTP & Webmail',
      description: 'Use your preferred email client, such as Thunderbird, Outlook or Apple Mail, or read your mail in the browser with our webmail.',
      webmailLink: 'Open webmail: webmail.itsh.dev'
    },
    storage: {
      title: '{storage} GB Pooled Storage',
      description: '{storage} GB storage, flexibly shared across all mailboxes. Expandable for {storageOverage}/GB per month.'
    },
    germanInfra: {
      title: 'German Infrastructure',
      description: 'Hosted in German data centers. Your data stays in the EU, and your mail is never scanned for ads.'
    },
    mailingLists: {
      title: 'Mailing Lists & Delegation',
      description: 'Create mailing lists for teams or projects. Delegate mailboxes with their own app passwords.'
    }
  },
  pricing: {
    title: 'Transparent Pricing',
    subtitle: 'One plan. Everything included. No hidden costs.',
    base: {
      label: 'Email Hosting',
      price: '€1.99',
      unit: 'per domain / month',
      annualPrice: '€19.90',
      annualUnit: 'per domain / year',
      annualSaving: '2 months free'
    },
    included: {
      label: 'Included',
      items: [
        'Up to 1,000 mailboxes & 5,000 aliases',
        '10 GB pooled storage',
        '1,000 outbound messages/month, over 30 per day',
        'DKIM / SPF / DMARC',
        'IMAP / SMTP / Webmail',
        'App passwords',
        'Mailing lists & delegation'
      ]
    },
    overages: {
      title: 'Additional Usage',
      storage: {
        label: 'Additional Storage',
        unit: 'per GB / month'
      },
      outbound: {
        label: 'Additional Messages',
        unit: 'per 1,000 messages'
      }
    },
    note: 'Cancel monthly. Prices include VAT.',
    noteAnnual: "Paid a year in advance. Cancel any time, one month's notice; paid time after that refunded pro rata. Additional usage is billed monthly. Prices include VAT."
  },
  comparison: {
    title: 'Why ITSH?',
    subtitle: 'See for yourself: up to 1,000 mailboxes at the price others charge for a single one.',
    callout: 'ITSH is the only provider in this comparison that includes up to 1,000 mailboxes per domain and runs its servers in Germany. And at mailbox.org you pay per mailbox: 5 mailboxes cost €12.50/month there; at ITSH, you stay at €1.99.',
    disclaimer: 'Prices and server locations based on publicly available provider information as of July 2026. Subject to change.',
    headers: {
      provider: 'Provider',
      price: 'Price/Month',
      mailboxes: 'Mailboxes',
      storage: 'Storage',
      customDomain: 'Custom Domain',
      germanServers: 'Servers in Germany'
    }
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: {
      q1: 'Can I use my existing domain?',
      a1: 'Yes. You can use any domain you own. If your domain is already registered with ITSH, setup is especially easy. For external domains, you just need to update the MX and TXT records.',
      q2: 'How many mailboxes can I create?',
      a2: "Up to 1,000 mailboxes and 5,000 aliases or lists per domain, such as info{'@'}, support{'@'}, max{'@'} and team{'@'}, all included. All mailboxes share the 10 GB pooled storage.",
      q3: 'Are 1,000 outbound messages per month enough?',
      a3: 'For most users and small teams, yes. That is over 30 emails per day. If you need more, additional messages cost {outboundOverage} per 1,000, flexible and without changing plans.',
      q4: 'How does DKIM/SPF/DMARC work?',
      a4: 'These email security standards protect your domain from spoofing and ensure your emails do not end up in spam. At ITSH, all three are configured automatically.',
      q5: 'Can I use my existing email client?',
      a5: 'Yes. ITSH Email supports JMAP, IMAP and SMTP. You can use Thunderbird, Outlook, Apple Mail or any other email client. App passwords are available for additional apps.',
      q6: 'Where are the servers located?',
      a6: 'Your email lives on servers in German data centers, with backups in the EU. ITSH is a German company, and you accept the data processing agreement (Art. 28 GDPR) directly in your account.',
      q7: 'Can I add more storage later?',
      a7: 'Yes. Additional storage costs {storageOverage} per GB per month. You can expand your storage at any time, no plan change or waiting required.',
      q8: 'Do special sending limits apply to new accounts?',
      a8: 'Yes. For the first 30 days after your customer account is registered you can send 30 messages per hour and 100 per day per mailbox. This keeps spam from freshly created accounts off the platform. Messages beyond that are not lost: they are temporarily rejected and stay in your email client. Depending on the client, delivery is retried automatically, otherwise you send it again. After 30 days the regular limits apply without you doing anything. If you are migrating existing mailboxes, you can have the restriction lifted early in the customer portal.'
    }
  },
  finalCta: {
    title: 'Professional Email in 5 Minutes',
    subtitle: 'Register for free and set up your email addresses. No setup fee, cancel monthly.'
  },
  domainInput: {
    placeholder: 'yourdomain.com',
    addToCart: 'Add to Cart',
    addedToCart: 'Added to cart',
    goToCart: 'Go to Cart',
    addAnother: 'Add another domain',
    noDomain: "Don't have a domain yet?",
    registerOne: 'Register one',
    error: {
      invalid: 'Please enter a valid domain name',
      taken: 'This domain already has active email hosting',
    }
  }
} as const

export const webhosting = {
  title: 'Web Hosting',
  paymentInterval: 'Payment interval',
  intervals: {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    halfAnnually: 'Half-yearly',
    annually: 'Annually'
  },
  intervalLabels: {
    monthly: 'monthly',
    quarterly: 'quarterly',
    halfAnnually: 'half-annually',
    annually: 'annually'
  },
  configurator: {
    eyebrow: 'All-inclusive · fully managed',
    heroTitle: 'Web hosting we take care of.',
    heroSubtitle: 'SSL, on-demand backups, email and DDoS protection are already in the price, and we keep the platform patched and running. Manage your hosting, domains and mail from a single panel.',
    startNow: 'Get started',
    entryPrice: 'Plans from {price} a month, cancel any time on one month\'s notice',
    viewMode: 'View',
    billing: 'Billing',
    guided: 'Recommended',
    allPlans: 'All plans',
    mostPopular: 'Most popular',
    planTaglines: {
      nano: 'Perfect for your first website',
      micro: 'For blogs and growing projects',
      mega: 'For demanding sites and shops',
      giga: 'For e-commerce and large projects',
      tera: 'For large portals and agencies',
      peta: 'Maximum performance for your project'
    },
    chips: {
      iso27001: 'ISO 27001 Data Centre',
      managedSelfService: 'Managed & Self-Service'
    },
    includedInEveryPlan: 'Included in every plan',
    includedFeatures: {
      sftpCron: 'SFTP access & cron jobs',
      backupsSsl: 'On-demand backups & SSL certificates',
      emailWebmail: 'Email mailboxes & webmail',
      phpDatabases: 'PHP 8.2 to 8.5 & MariaDB'
    },
    specsBand: {
      title: 'Included in all plans',
      sslIncluded: 'SSL included',
      sslDesc: 'Free TLS/SSL certificates',
      uptime: '99% Availability',
      uptimeDesc: 'Guaranteed uptime SLA',
      backups: 'On-Demand Backups',
      backupsDesc: 'Up to 5 per site, kept for 72 hours',
      emailWebmail: 'Email & Webmail',
      emailWebmailDesc: 'Up to 1,000 mailboxes per domain',
      php: 'PHP 8.2 – 8.5',
      phpDesc: 'Latest version from day one',
      ddos: 'DDoS Protection',
      ddosDesc: 'Enterprise-level protection',
      traffic: 'Unlimited¹ Traffic',
      trafficDesc: 'No data limits (fair use)',
      monitoring: '24/7 Monitoring',
      monitoringDesc: 'Proactive monitoring',
      cancellable: 'Monthly cancellable',
      cancellableDesc: 'Flexible contract term',
      fairUseNote: '¹ Unlimited under our fair-use policy — normal usage without abuse. Adjustment may be required for sustained, excessive usage.'
    },
    storageSelector: 'Storage requirement',
    storageOverage: 'Use more storage than your package includes and we bill the excess at €0.05 net per GB per month (5.95 cents incl. 19% VAT). Files, databases and mailboxes of every site in the package count towards it.',
    contactSales: {
      title: 'Need more storage?',
      text: 'For requirements over 2 TB, we will create an individual offer for you.',
      cta: 'Contact us'
    },
    allPlansTable: {
      title: 'All hosting plans at a glance',
      subtitle: 'Compare all plans and find the right package for your project.',
      package: 'Package',
      price: 'Price',
      storage: 'Storage',
      support: 'Support',
      backups: 'Backups',
      ssl: 'SSL',
      php: 'PHP',
      choose: 'Add to cart'
    },
    trust: {
      gdpr: 'DPA under Art. 28 GDPR',
      uptime: '99% Uptime',
      germanDatacenters: 'German data centres'
    }
  },
  productCard: {
    addedToCart: 'Added to cart!',
    mainFeatures: 'Main features',
    storage: 'Storage',
    support: 'Support',
    paymentMethod: 'Payment method',
    flexibleTerms: 'Flexible terms',
    cancellablePeriod: 'Cancel any time, one month\'s notice',
    addToCart: 'Add to cart'
  }
} as const

export const comingSoon = {
  badge: 'Coming soon',
  dashboardNote: 'This product is not yet available for ordering. We will notify you when it launches.'
} as const

export const pricingCalculator = {
  title: 'Pricing Calculator',
  eyebrow: 'Transparent Pricing',
  subtitle: 'Estimate your monthly cloud costs — transparent, with no hidden fees.',
  cta: 'Get started',
  ctaCart: 'Add to cart',
  ctaRegister: 'or create an account first',
  domainNote: 'Domain prices are available in our {link}.',
  domainCatalogLink: 'TLD catalog',
  heroChips: {
    transparent: 'Total incl. VAT',
    noHiddenFees: 'No hidden fees',
    payg: 'Pay-as-you-go'
  },
  categoriesLabel: 'Select products',
  presets: {
    label: 'Quick start',
    smallApp: 'Small App / API',
    smallAppDesc: '1 vCPU, 2 GiB RAM, 10 GiB storage',
    webPlatform: 'Web Platform',
    webPlatformDesc: '4 vCPU, 8 GiB RAM, 50 GiB storage',
    teamNamespace: 'Team Namespace',
    teamNamespaceDesc: 'Namespace M + Managed DB',
    custom: 'Custom',
    customDesc: 'Configure from scratch'
  },
  categories: {
    k8sPayg: 'Kubernetes PAYG',
    s3: 'Object Storage',
    k8sNamespace: 'K8s Namespaces',
    k8sCluster: 'K8s Clusters',
    k8sAddon: 'K8s Addons',
    vserver: 'VServer',
    webhosting: 'Web Hosting',
    email: 'Email Hosting'
  },
  k8sPayg: {
    title: 'Kubernetes Pay-as-you-go',
    description: 'Pay only for what you use. Ideal for variable workloads, CI/CD pipelines, or development environments.',
    cpuCores: 'CPU Cores',
    cpuHint: 'Typical: 0.5–2 for small apps, 4–8 for databases',
    cpuHours: 'CPU Hours/Month',
    cpuHoursHint: '730 = 24/7 · Less for intermittent workloads',
    ramGib: 'RAM (GiB)',
    ramHint: 'Typical: 1–4 GiB for web apps, 8–32 GiB for caches/DBs',
    ramHours: 'RAM Hours/Month',
    ramHoursHint: '730 = 24/7 · Less for intermittent workloads',
    storageGib: 'Storage (GiB)',
    storageHint: 'Persistent volumes for databases, uploads, etc.',
    egressGib: 'Egress (GiB/Month)',
    egressHint: 'Outbound traffic (API responses, downloads). Internal traffic free.',
    baseFee: 'Base fee',
    fullMonth: '730 = 24/7'
  },
  s3: {
    title: 'Object Storage',
    description: 'S3-compatible object storage for backups, media, and static files.',
    storageGb: 'Storage (GB)',
    storageHint: 'Total stored data volume',
    egressGb: 'Egress (GB/Month)',
    egressHint: 'Downloads and outbound traffic per month',
    baseFee: 'Base fee'
  },
  k8sNamespace: {
    title: 'Kubernetes Namespaces',
    description: 'Shared cluster with guaranteed resources. Ideal for teams that don\'t want to manage their own cluster.',
    tier: 'Tier',
    quantity: 'Quantity',
    specs: '{cpu}, {ram}'
  },
  k8sCluster: {
    title: 'Kubernetes Clusters',
    description: 'Dedicated cluster with full control. For businesses with specific security or compliance requirements.',
    clusterType: 'Cluster Type',
    extraWorkers: 'Extra Workers',
    extraWorkersHint: 'Additional worker nodes for more capacity',
    perWorker: 'per worker'
  },
  k8sAddon: {
    title: 'Kubernetes Addons',
    description: 'Optional add-on services for your Kubernetes namespace.',
    quantity: 'Quantity'
  },
  vserver: {
    title: 'VServer',
    description: 'Premium KVM VPS on NVMe, snapshots and support included. Pick a size and quantity.',
    tier: 'Size',
    quantity: 'Quantity',
    specs: '{cpu} vCPU · {ram} GB RAM · {disk} GB NVMe',
    modePreset: 'Fixed sizes (S–XL)',
    modeCustom: 'Custom shape',
    customDescription: 'Configure vCPU, RAM and disk freely. RAM and disk ranges scale with your vCPU choice.',
    vcpu: 'vCPU',
    ram: 'RAM (GB)',
    disk: 'Disk (GB)',
    boundsHint: 'At {vcpu} vCPU: {min}–{max} GB',
    breakdownGross: 'incl. VAT'
  },
  webhosting: {
    title: 'Web Hosting',
    description: 'Managed web hosting with SSD storage, email and on-demand backups.',
    tier: 'Package',
    quantity: 'Quantity',
    storage: 'Storage'
  },
  email: {
    title: 'Email Hosting',
    description: 'Up to 1,000 mailboxes on your own domain. Storage and outbound included, overage billed by usage.',
    domains: 'Domains',
    domainsHint: '{price} €/month per domain (gross)',
    storageGb: 'Total storage (GB)',
    storageHint: '{included} GB included ({perDomain} GB per domain)',
    outbound: 'Outbound emails/month',
    outboundHint: '{included} included ({perDomain} per domain)',
    baseFee: 'Base fee/domain',
    messages: 'messages'
  },
  summary: {
    title: 'Summary',
    subtotal: 'Subtotal',
    total: 'Total',
    perMonth: '/ month',
    vatToggle: 'Gross (incl. VAT)',
    net: 'net',
    gross: 'gross',
    vat: 'VAT (19%)',
    emptyState: 'Select products to get an estimate.',
    estimateNote: 'Non-binding estimate — custom sizes are configured in the dashboard after registration.',
    shareLink: 'Copy estimate link',
    linkCopied: 'Link copied',
    linkCopiedDesc: 'Share this estimate with your team.',
    productCount: '{count} product | {count} products',
    emailNeedsDomainTitle: 'Choose a domain for email',
    emailNeedsDomainMessage: 'Email hosting needs a domain. Please select one on the email page to add it to your cart.'
  }
} as const
